import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  PackageOpen, Star, ChevronRight, Zap, Filter 
} from 'lucide-react'; 

const NODE_API_URL = 'http://localhost:2026';

interface Product {
  id: number;
  name: string;
  price: number;
  unit: string;
  moq: string;
  description: string;
  companyName: string;
  image: string;
  rating: string;
  reviewCount: number;
  discount: string;
  seller_id: number;
}

const CategoryProductsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categoryName, setCategoryName] = useState('Category Products'); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${NODE_API_URL}/api/products/category/${id}`);
        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        
        if (data.products) {
            setProducts(data.products);
            setCategoryName(data.categoryName); 
        } else {
            setProducts(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        setError('Unable to load products.');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProducts();
  }, [id]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(price);
  };

  if (loading) return (
    <div className="min-h-screen flex justify-center items-center bg-gray-50">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-red-600"></div>
    </div>
  );

  return (
    <div className="bg-[#f2f2f2] min-h-screen pb-10 font-sans">
      
      {/* 1. COMPACT BREADCRUMB & HEADER */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 py-3">
          <div className="flex items-center text-[11px] text-gray-500 gap-2 mb-2">
            <Link to="/" className="hover:text-red-600">Home</Link> <ChevronRight size={10} /> 
            <span className="text-gray-900 font-medium">{categoryName}</span>
          </div>
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-800 uppercase tracking-tight">{categoryName}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto mt-4 px-2 sm:px-4">
        <main>
          {error ? (
            <div className="bg-white p-10 text-center rounded border border-red-100 text-red-600 font-bold">{error}</div>
          ) : products.length === 0 ? (
            <div className="bg-white p-20 text-center rounded border border-dashed border-gray-300">
               <PackageOpen size={48} className="mx-auto text-gray-300 mb-4" />
               <p className="text-gray-500 font-bold">No products found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
              {products.map((product) => (
                <div key={product.id} className="bg-white border border-gray-200 rounded hover:shadow-md transition-all group flex flex-col">
                  
                  {/* Product Image Area - Matching Store Style */}
                  <div className="h-40 p-3 flex items-center justify-center bg-white relative overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" 
                    />
                    {/* Green Badge Top Left */}
                    <div className="absolute top-2 left-2 bg-green-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                      {product.discount || '8% OFF'}
                    </div>
                  </div>

                  {/* Product Info Area */}
                  <div className="p-3 border-t border-gray-100 flex-grow flex flex-col justify-between">
                    <div>
                      <h3 className="text-xs text-gray-700 font-medium line-clamp-2 h-8 leading-relaxed mb-1 group-hover:text-red-600 transition-colors">
                        {product.name}
                      </h3>
                      
                      {/* Star Rating Layout */}
                      <div className="flex items-center gap-1 mb-2">
                         <div className="flex text-yellow-400">
                           <Star size={10} fill="currentColor"/>
                           <Star size={10} fill="currentColor"/>
                           <Star size={10} fill="currentColor"/>
                           <Star size={10} fill="currentColor"/>
                         </div>
                         <span className="text-[10px] text-gray-400">({product.reviewCount || 24})</span>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base font-black text-gray-900">{formatPrice(product.price)}</span>
                        <span className="text-[10px] text-gray-400 line-through font-medium tracking-tighter">
                          ₹{Math.round(product.price * 1.08)}
                        </span>
                      </div>
                      
                      {/* Ships in 24 Hours Tag */}
                      <div className="text-[10px] text-green-600 font-bold mb-3 flex items-center gap-1">
                        <Zap size={10} fill="currentColor"/> Ships in 24 Hours
                      </div>

                      {/* Main Red Button */}
                      <button 
                        onClick={() => navigate(`/product/${product.id}`)} 
                         className="block w-full text-center bg-white border border-red-600 text-red-600 py-1.5 rounded text-[11px] font-bold hover:bg-red-600 hover:text-white transition-all uppercase"
                      >
                        VIEW DETAILS
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CategoryProductsPage;